import { motion } from "framer-motion";
import { MoveRight, ArrowRight } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import { GhostButton } from "@/components/Buttons";
import { scrollToId } from "@/lib/ui";

const CARDS = [
  {
    step: "01",
    title: "POSITION",
    question: "Where is price?",
    desc: "Before looking for a trade, determine whether market context qualifies. Position defines where an opportunity may exist and where you should be paying attention.",
    testid: "gameplan-card-position",
  },
  {
    step: "02",
    title: "EVENT",
    question: "What happened?",
    desc: "A position alone is not enough. A predefined market event must occur before an opportunity progresses. The Event gives the trader a reason to prepare for execution.",
    testid: "gameplan-card-event",
  },
  {
    step: "03",
    title: "ENTRY",
    question: "When do I act?",
    desc: "The Entry defines when the conditions of the Game Plan have been satisfied and execution becomes appropriate.",
    testid: "gameplan-card-entry",
  },
];

function Connector() {
  return (
    <div className="flex items-center justify-center py-2 lg:py-0">
      <motion.div
        animate={{ x: [0, 8, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="text-neon"
      >
        <MoveRight size={26} className="hidden lg:block" />
        <MoveRight size={26} className="rotate-90 lg:hidden" />
      </motion.div>
    </div>
  );
}

export default function GamePlanV2() {
  return (
    <section id="framework" data-testid="gameplan-section" className="border-t border-line bg-[#070809] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Reveal>
            <div className="mb-5 flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon/90">
              <span className="h-px w-8 bg-neon/50" />
              02 — The Methodology
              <span className="h-px w-8 bg-neon/50" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Every trade needs
              <br />a <span className="glow-text-neon text-neon">game plan.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-mist">
              P.E.E. stands for:{" "}
              <span className="font-mono text-sm uppercase tracking-[0.14em] text-white">
                Position. Event. Entry.
              </span>{" "}
              These three components form the foundation of the P.E.E. Game Plan.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch">
          {CARDS.map((c, i) => (
            <div key={c.step} className="contents">
              <Reveal delay={0.12 * i} className="h-full">
                <motion.div
                  data-testid={c.testid}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="group flex h-full flex-col rounded-lg border border-line bg-charcoal p-8 transition-colors duration-500 hover:border-neon/50"
                >
                  <div className="font-mono text-xs tracking-[0.3em] text-neon">
                    {c.step}
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                    {c.title}
                  </h3>
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-mist">
                    {c.question}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-mist">{c.desc}</p>
                  <div className="mt-auto pt-8">
                    <div className="h-px w-full bg-line transition-colors duration-500 group-hover:bg-neon/40" />
                  </div>
                </motion.div>
              </Reveal>
              {i < CARDS.length - 1 && <Connector />}
            </div>
          ))}
        </div>

        <div className="mt-20 space-y-3 text-center">
          {[
            { text: "Position tells you where to look.", tone: "text-white" },
            { text: "Event tells you what to wait for.", tone: "text-white" },
            { text: "Entry tells you when to act.", tone: "glow-text-neon text-neon" },
          ].map((l, i) => (
            <Reveal key={l.text} delay={0.12 * i}>
              <div
                className={`font-display text-2xl font-extrabold uppercase tracking-tight sm:text-4xl ${l.tone}`}
              >
                {l.text}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mx-auto mt-12 max-w-2xl text-center text-base leading-relaxed text-mist">
            But P.E.E. is more than three words. Members learn the complete
            P.E.E. Game Plan—including qualifying market positions, predefined
            events, entries, risk, targets, validation, invalidation, session
            rules, trade management, and the execution process surrounding them.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-12 text-center">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white sm:text-sm">
              No qualifying P.E.E. <span className="mx-2 text-neon">—</span> No trade.
            </div>
            <GhostButton
              testid="gameplan-cta"
              onClick={() => scrollToId("how-it-works")}
              className="mt-8"
            >
              Learn the Game Plan
              <ArrowRight size={16} />
            </GhostButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
